// rraVerificationUi — the PURE logic behind the console's Verification panel
// (RRA v2.1 / W3; docs/design/rra-v2-1-implementation.md §3.W3.6, W3.7, W3.12).
//
// Everything a test can check lives here, with NO api / React import (type-only
// imports are erased), so `npx tsx --test src/lib/rraVerificationUi.test.ts`
// runs without the Vite-only `import.meta.env` that lib/api.ts reads:
//
//   • the in-gap question set and the verifier-facing "need object" for the
//     items an ask covers (E2),
//   • the answer selection → wire body, with the SAME required-field rules the
//     API applies (position-to-know first; with Yes every in-gap prong; with
//     No the prongs are dropped),
//   • the reply / document / signed-permission drafts → wire bodies (A3, A10),
//   • the words the card shows: pills, response line, front-line timeline rows,
//     pause / stop lines, pending-address / pending-number lines (W3.12).
//
// STRINGS: consumer-facing wording comes from rraCopy (byte-identical to the
// API copy; never edited here). The staff-console strings below are DRAFT until
// Miles approves the copy pack; each is marked and listed in the W3 fragment.
//
// There is NO free-text field in any of these drafts (the one note, G4, is typed
// on the call sheet and only shown — and removed — here).

import {
  ANSWER_STAFF,
  ATTENTION,
  CALL_OUTCOME_SHORT,
  cantHelpReasonLabel,
  verifierNeedObject,
} from './rraCopy';
import type {
  RecordRraPermissionBody,
  RecordRraReplyBody,
  RraCaseView,
  RraFrontlineCallView,
  RraFrontlineView,
  RraItemView,
  RraProngAnswer,
  RraRequesterNoteKind,
  RraResponseChannel,
  RraStaffDocumentKind,
  RraVerificationView,
  RraVerifierView,
} from './rraStaffClient';

// ---------------------------------------------------------------------------
// Dates (display only)
// ---------------------------------------------------------------------------

/** "Oct 23, 2026" — a calendar date; an unparseable value is shown as-is. */
export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Oct 23, 2:10 PM" — a call's date AND time (when a call happened matters). */
export function formatWhen(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Today's calendar date as YYYY-MM-DD in the browser's zone (a date input's value). */
export function todayYmd(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// The gap an ask covers (E2) — only these questions may be asked or recorded
// ---------------------------------------------------------------------------

export interface GapFlags {
  askDisability: boolean;
  askNeed: boolean;
}

type GapItem = Pick<RraItemView, 'itemId' | 'scope' | 'evaluation'>;

const DISABILITY_SCOPES: ReadonlySet<string> = new Set(['disability_and_need', 'disability_only']);
const NEED_SCOPES: ReadonlySet<string> = new Set(['disability_and_need', 'need_only']);

/** The union of the recorded gaps across these items (an item with no recorded gap adds nothing). */
export function gapOfItems(items: GapItem[]): GapFlags {
  let askDisability = false;
  let askNeed = false;
  for (const item of items) {
    const ev = item.evaluation;
    if (!ev || ev.outcome !== 'gap' || !ev.gapScope) continue;
    if (DISABILITY_SCOPES.has(ev.gapScope)) askDisability = true;
    if (NEED_SCOPES.has(ev.gapScope)) askNeed = true;
  }
  return { askDisability, askNeed };
}

/** The gap one ask covers: the union over the items it names. */
export function gapOfVerification(items: GapItem[], itemIds: string[]): GapFlags {
  const wanted = new Set(itemIds);
  return gapOfItems(items.filter((i) => wanted.has(i.itemId)));
}

/**
 * The verifier-facing need object (E2) for the covered items whose NEED is in the
 * gap: animal items vs everything else (rraCopy.verifierNeedObject).
 */
export function needObjectForItems(items: GapItem[]): string {
  let animalCount = 0;
  let otherCount = 0;
  for (const item of items) {
    const ev = item.evaluation;
    if (!ev || ev.outcome !== 'gap' || !ev.gapScope || !NEED_SCOPES.has(ev.gapScope)) continue;
    if (item.scope === 'esa_animal' || item.scope === 'service_animal') animalCount += 1;
    else otherCount += 1;
  }
  return verifierNeedObject({ animalCount, otherCount });
}

/**
 * The name the question is asked about: the household member when the case is for
 * someone else, else the requester, else whoever signed the permission, else a
 * plain "the requester" (the same order the API uses).
 */
export function subjectNameOf(record: {
  subject?: { kind: 'self' } | { kind: 'household_member'; name: string } | null;
  requester: { name?: string };
  authorization: { signedName?: string };
}): string {
  const s = record.subject;
  if (s && s.kind === 'household_member' && s.name.trim()) return s.name.trim();
  return record.requester.name?.trim() || record.authorization.signedName?.trim() || 'the requester';
}

// ---------------------------------------------------------------------------
// Answers (E2) — Yes · No · They couldn't say
// ---------------------------------------------------------------------------

export type AnswerChoice = RraProngAnswer | '';

export interface AnswerSelection {
  /** "Do you know {who} in a way that lets you answer?" */
  position: 'yes' | 'no' | '';
  disability: AnswerChoice;
  need: AnswerChoice;
}

export const BLANK_ANSWERS: AnswerSelection = { position: '', disability: '', need: '' };

export const ANSWER_FIRST_MESSAGE = 'Please answer the first question.'; // DRAFT
export const ANSWER_EACH_MESSAGE = 'Please answer each question.'; // DRAFT

/** Why these answers cannot be recorded yet, or null when they can. */
export function answerError(sel: AnswerSelection, gap: GapFlags): string | null {
  if (sel.position === '') return ANSWER_FIRST_MESSAGE;
  if (sel.position === 'no') return null;
  if (gap.askDisability && sel.disability === '') return ANSWER_EACH_MESSAGE;
  if (gap.askNeed && sel.need === '') return ANSWER_EACH_MESSAGE;
  return null;
}

export interface AnswerBody {
  positionToKnow: 'yes' | 'no';
  disability?: RraProngAnswer;
  need?: RraProngAnswer;
}

/**
 * The wire answers: position-to-know always; with Yes only the IN-GAP prongs
 * (an out-of-gap answer is never sent); with No no prongs at all. Null while
 * `answerError` says something is missing.
 */
export function answerBody(sel: AnswerSelection, gap: GapFlags): AnswerBody | null {
  if (answerError(sel, gap) !== null) return null;
  if (sel.position === 'no') return { positionToKnow: 'no' };
  return {
    positionToKnow: 'yes',
    ...(gap.askDisability && sel.disability !== '' ? { disability: sel.disability } : {}),
    ...(gap.askNeed && sel.need !== '' ? { need: sel.need } : {}),
  };
}

/** The staff word for one prong's answer. */
export function answerWord(a: RraProngAnswer | undefined): string {
  switch (a) {
    case 'yes':
      return ANSWER_STAFF.yes;
    case 'no':
      return ANSWER_STAFF.no;
    case 'cant_say':
      return ANSWER_STAFF.cantSay;
    default:
      return '—';
  }
}

// ---------------------------------------------------------------------------
// A recorded response (the card's response line)
// ---------------------------------------------------------------------------

/** How the answer arrived, in words (ATTENTION.verifierAnswered uses the same words). */
export const RESPONSE_CHANNEL_WORDS: Record<RraResponseChannel, string> = {
  form: 'on the online form',
  oral: 'by phone',
  reply_email: 'by reply email',
  letter: 'by letter',
  uploaded_document: 'with a document',
};

type ResponseView = NonNullable<RraVerificationView['response']>;

/**
 * "Responded Oct 23, 2026 by phone · In a position to know: Yes · Disability: Yes
 * · Need: They couldn't say". A prong is listed only when it was answered.
 */
export function responseLine(r: ResponseView, fmt: (iso: string) => string = formatDate): string {
  const parts: string[] = [`Responded ${fmt(r.submittedAt)} ${RESPONSE_CHANNEL_WORDS[r.channel] ?? ''}`.trim()];
  if (r.positionToKnow !== undefined) {
    parts.push(`In a position to know: ${r.positionToKnow ? ANSWER_STAFF.yes : ANSWER_STAFF.no}`);
  }
  if (r.disabilityAnswer !== undefined) parts.push(`Disability: ${answerWord(r.disabilityAnswer)}`);
  if (r.needAnswer !== undefined) parts.push(`Need: ${answerWord(r.needAnswer)}`);
  return parts.join(' · ');
}

/** Did this verifier answer No to anything? (offers the staff-sent "wasn't able to confirm" note) */
export function answeredNo(v: Pick<RraVerificationView, 'response'>): boolean {
  const r = v.response;
  return Boolean(r && (r.disabilityAnswer === 'no' || r.needAnswer === 'no'));
}

export const RECEIVED_AFTER_WITHDRAWAL = 'Received after permission was withdrawn — not used.'; // DRAFT

// ---------------------------------------------------------------------------
// Ask state
// ---------------------------------------------------------------------------

/** The ask's cancel reason when a withdrawal of permission cancelled it (W2 stamps this). */
export const WITHDRAWAL_CANCEL_REASON = 'Permission to contact withdrawn';

/** Front line already reported a "spoke" call (the answers live on the oral row for a no-email ask). */
export function answeredByPhone(v: Pick<RraVerificationView, 'frontline'>): boolean {
  return Boolean(v.frontline?.calls.some((c) => c.outcome === 'spoke'));
}

/** Still awaiting an answer: no response, not cancelled, not answered by phone. */
export function isOpenAsk(v: RraVerificationView): boolean {
  return !v.response && v.status !== 'cancelled' && !v.cancelledAt && !answeredByPhone(v);
}

/** An ask a withdrawal cancelled — a reply that still arrives is recorded but never used. */
export function cancelledByWithdrawal(v: Pick<RraVerificationView, 'cancelledAt' | 'cancelReason'>): boolean {
  return Boolean(v.cancelledAt) && v.cancelReason === WITHDRAWAL_CANCEL_REASON;
}

/** "Record a reply or letter…" is offered on an open ask and on one a withdrawal cancelled. */
export function canRecordReply(v: RraVerificationView): boolean {
  if (v.response) return false;
  return isOpenAsk(v) || cancelledByWithdrawal(v);
}

/** "Send" — only for an ask that was created but not yet sent, and only with an email. */
export function canSend(v: RraVerificationView): boolean {
  return v.status === 'created' && Boolean(v.verifier.email) && isOpenAsk(v);
}

// ---------------------------------------------------------------------------
// Pills
// ---------------------------------------------------------------------------

export interface VerificationPill {
  text: string;
  tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  title?: string;
}

/** The pills on a card: status; Bounced; Call suggested (only without front line); 3 calls without reaching them. */
export function verificationPills(v: RraVerificationView): VerificationPill[] {
  const pills: VerificationPill[] = [
    {
      text: v.status,
      tone: v.status === 'responded' ? 'success' : v.status === 'cancelled' ? 'neutral' : 'info',
    },
  ];
  const live = !v.response && v.status !== 'cancelled';
  if (v.bouncedAt && !v.response) {
    pills.push({
      text: 'Bounced',
      tone: 'danger',
      title:
        'The email bounced — reminders stopped. Reach them another way or ask the requester for an alternate contact.',
    });
  }
  if (v.staffCallSuggestedAt && live && !v.frontline) {
    pills.push({
      text: 'Call suggested',
      tone: 'warning',
      title:
        'Reminders are exhausted — call the verifier, or ask the requester for an alternate contact. Nothing closes on silence.',
    });
  }
  if (v.frontline?.capReachedAt && live && !answeredByPhone(v)) {
    pills.push({ text: '3 calls without reaching them', tone: 'warning' });
  }
  return pills;
}

/** The short word for each requester note kind (the card's "Sent to the requester" line). DRAFT. */
export const REQUESTER_NOTE_LABEL: Record<RraRequesterNoteKind, string> = {
  still_open: 'Still open',
  wants_to_hear_from_you: 'Wants to hear from them',
  own_release_form: "Office's own form",
  phone_number: 'Phone number',
  verifier_no: "Wasn't able to confirm",
};

export function requesterNotesLine(
  notes: RraVerificationView['requesterNotes'],
  fmt: (iso: string) => string = formatDate,
): string | null {
  if (!notes || notes.length === 0) return null;
  return `Sent to the requester: ${notes.map((n) => `${REQUESTER_NOTE_LABEL[n.kind]} (${fmt(n.sentAt)})`).join(', ')}`;
}

// ---------------------------------------------------------------------------
// The front-line timeline (FrontlineTimeline)
// ---------------------------------------------------------------------------

export interface CallNames {
  requesterName: string;
  signerName: string;
}

/** "Front line · asked automatically · Oct 23, 2026" / "… asked by Andrea …". */
export function frontlineHeader(f: Pick<RraFrontlineView, 'triggeredByDisplay' | 'triggeredAt'>, fmt: (iso: string) => string = formatDate): string {
  const who = f.triggeredByDisplay === 'Automatic' ? 'automatically' : `by ${f.triggeredByDisplay}`;
  return `Front line · asked ${who} · ${fmt(f.triggeredAt)}`;
}

/** "Next call Oct 24, 2026" while a call is suggested; null otherwise. */
export function nextCallLine(f: Pick<RraFrontlineView, 'nextCallSuggestedAt'>, fmt: (iso: string) => string = formatDate): string | null {
  return f.nextCallSuggestedAt ? `Next call ${fmt(f.nextCallSuggestedAt)}` : null;
}

/**
 * One call, newest-first row: "{callerName} · {when} · {outcome}{: reason}{ · call back <when>}".
 * The note is NOT part of this line (it sits on its own muted line, with Remove note).
 */
export function callRowText(
  c: Pick<RraFrontlineCallView, 'callerName' | 'at' | 'outcome' | 'cantHelpReason' | 'callBackAt'>,
  names: CallNames,
  fmtWhen: (iso: string) => string = formatWhen,
): string {
  let line = `${c.callerName} · ${fmtWhen(c.at)} · ${CALL_OUTCOME_SHORT[c.outcome]}`;
  if (c.cantHelpReason) line += `: ${cantHelpReasonLabel(c.cantHelpReason, names)}`;
  if (c.callBackAt) line += ` · call back ${fmtWhen(c.callBackAt)}`;
  return line;
}

/** "Note removed by Andrea · Oct 23, 2026" — what a removed note reads. DRAFT. */
export function noteRemovedLine(
  removed: NonNullable<RraFrontlineCallView['noteRemoved']>,
  fmt: (iso: string) => string = formatDate,
): string {
  return `Note removed by ${removed.byDisplay} · ${fmt(removed.at)}`;
}

export interface FrontlineStatusLine {
  kind: 'paused' | 'stopped';
  text: string;
}

/**
 * Why calls are not being suggested, in a sentence: stopped (they can't answer /
 * already answered / the request changed), paused for staff (their office's own
 * release form / front line needs you), or paused until a date (they want to
 * hear from the requester first). At most one, in that order; a `pausedUntil`
 * already past is not shown (the watcher re-arms it). Each carries Resume calls. DRAFT.
 */
export function frontlineStatusLine(
  f: Pick<RraFrontlineView, 'stopped' | 'pausedForStaff' | 'pausedUntil'>,
  requesterName: string,
  now: number = Date.now(),
  fmt: (iso: string) => string = formatDate,
): FrontlineStatusLine | null {
  if (f.stopped) {
    const text =
      f.stopped.reason === 'cannot_answer_or_declined'
        ? "Calls stopped: they can't answer from what they know, or they declined."
        : f.stopped.reason === 'says_already_answered'
          ? 'Calls stopped: they say they already answered.'
          : 'Calls stopped: the request was revised.';
    return { kind: 'stopped', text };
  }
  if (f.pausedForStaff) {
    return {
      kind: 'paused',
      text:
        f.pausedForStaff.reason === 'needs_own_release'
          ? "Calls paused: waiting on their office's own release form."
          : 'Calls paused: front line needs you.',
    };
  }
  if (f.pausedUntil && Date.parse(f.pausedUntil) > now) {
    return {
      kind: 'paused',
      text: `Calls paused until ${fmt(f.pausedUntil)}: they want to hear from ${requesterName} first.`,
    };
  }
  return null;
}

/** "They asked for it in writing at {email}." and the confirm button's label. DRAFT / ATTENTION. */
export function pendingWritingLine(email: string): { text: string; button: string } {
  return {
    text: `They asked for it in writing at ${email}.`,
    button: ATTENTION.confirmWritingAddress(email),
  };
}

/** "Front line was given a new number: {phone}." and the button's label. DRAFT. */
export function pendingPhoneLine(phone: string): { text: string; button: string } {
  return { text: `Front line was given a new number: ${phone}.`, button: 'Use this number' };
}

// ---------------------------------------------------------------------------
// The create block (F5)
// ---------------------------------------------------------------------------

export const NO_CONTACT_NOTICE = "No email or phone on file — we'll ask the requester for a number."; // DRAFT
export const NO_EMAIL_NOTICE = 'No email on file for this contact — front line will be asked to phone them.';

/** The notice under the verifier picker: nothing to reach them by, or no email (front line will phone). */
export function contactNotice(v: Pick<RraVerifierView, 'email' | 'phone'> | undefined): string | null {
  if (!v) return null;
  if (!v.email && !v.phone) return NO_CONTACT_NOTICE;
  if (!v.email) return NO_EMAIL_NOTICE;
  return null;
}

/** The line shown on the card under the name: "email · phone". */
export function contactLine(v: Pick<RraVerifierView, 'email' | 'phone'>): string {
  return `${v.email ?? 'no email'} · ${v.phone ?? 'no phone'}`;
}

// ---------------------------------------------------------------------------
// Files (the quarantined two-step upload)
// ---------------------------------------------------------------------------

export const MAX_PDF_BYTES = 50 * 1024 * 1024;

/** Why this file cannot be added, or null. The server re-checks that it parses as a PDF. */
export function pdfFileError(file: { name: string; type: string; size: number } | null): string | null {
  if (!file) return 'Please choose a PDF.'; // DRAFT
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!isPdf) return 'The file must be a PDF.'; // DRAFT
  if (file.size <= 0) return 'That file is empty.'; // DRAFT
  if (file.size > MAX_PDF_BYTES) return 'The file is too large (max 50 MB).'; // DRAFT
  return null;
}

// ---------------------------------------------------------------------------
// A3 — "Record a reply or letter…"
// ---------------------------------------------------------------------------

export type ReplyChannel = RecordRraReplyBody['channel'];

export const REPLY_CHANNEL_OPTIONS: ReadonlyArray<{ value: ReplyChannel; label: string }> = [
  { value: 'reply_email', label: 'Reply email' }, // DRAFT
  { value: 'letter', label: 'Letter' }, // DRAFT
  { value: 'uploaded_document', label: 'A document they sent' }, // DRAFT
];

export interface ReplyDraft {
  channel: ReplyChannel | '';
  /** YYYY-MM-DD — "When we received it". */
  receivedOn: string;
  answers: AnswerSelection;
}

export function blankReplyDraft(now: Date = new Date()): ReplyDraft {
  return { channel: '', receivedOn: todayYmd(now), answers: { ...BLANK_ANSWERS } };
}

export const REPLY_CHANNEL_MESSAGE = 'Please choose how it came in.'; // DRAFT
export const REPLY_DATE_MESSAGE = 'Please enter when we received it.'; // DRAFT
export const REPLY_FUTURE_MESSAGE = "The date can't be in the future."; // DRAFT

/** Why the reply cannot be recorded yet, or null. */
export function replyError(draft: ReplyDraft, gap: GapFlags, now: Date = new Date()): string | null {
  if (draft.channel === '') return REPLY_CHANNEL_MESSAGE;
  if (!YMD_RE.test(draft.receivedOn)) return REPLY_DATE_MESSAGE;
  if (draft.receivedOn > todayYmd(now)) return REPLY_FUTURE_MESSAGE;
  return answerError(draft.answers, gap);
}

/**
 * When we received it, as an instant: noon that day (browser zone), never later
 * than `now` (the server refuses a future receivedAt, and "today" at noon may be ahead).
 */
export function receivedAtFromDate(ymd: string, now: Date = new Date()): string {
  const noon = new Date(`${ymd}T12:00:00`);
  return (noon.getTime() > now.getTime() ? now : noon).toISOString();
}

/** The wire body, or null while `replyError` says something is missing. */
export function replyBody(
  draft: ReplyDraft,
  gap: GapFlags,
  opts: { now?: Date; staffDocumentIndex?: number } = {},
): RecordRraReplyBody | null {
  const now = opts.now ?? new Date();
  if (replyError(draft, gap, now) !== null || draft.channel === '') return null;
  const answers = answerBody(draft.answers, gap);
  if (!answers) return null;
  return {
    channel: draft.channel,
    receivedAt: receivedAtFromDate(draft.receivedOn, now),
    ...answers,
    ...(opts.staffDocumentIndex !== undefined ? { staffDocumentIndex: opts.staffDocumentIndex } : {}),
  };
}

// ---------------------------------------------------------------------------
// A3 / A10 — "Add a letter or document…" and the signed-permission follow-up
// ---------------------------------------------------------------------------

export const DOCUMENT_KIND_OPTIONS: ReadonlyArray<{ value: RraStaffDocumentKind; label: string }> = [
  { value: 'letter', label: 'A letter from someone who knows them' }, // DRAFT
  { value: 'signed_permission', label: 'A signed permission to contact someone' }, // DRAFT
  { value: 'document', label: 'Another document' }, // DRAFT
];

/** The kind word beside a document in the list. DRAFT. */
export const DOCUMENT_KIND_WORD: Record<RraStaffDocumentKind, string> = {
  letter: 'Letter',
  reply_email: 'Reply email',
  document: 'Document',
  signed_permission: 'Signed permission',
};

export const MAX_PERMISSION_PEOPLE = 5;

export interface PermissionPersonDraft {
  name: string;
  role: string;
  email: string;
  phone: string;
}

export interface PermissionDraft {
  signedName: string;
  /** YYYY-MM-DD — "Date signed". */
  signedOn: string;
  signer: 'subject' | 'parent_guardian' | '';
  people: PermissionPersonDraft[];
}

export const blankPermissionPerson = (): PermissionPersonDraft => ({ name: '', role: '', email: '', phone: '' });

export function blankPermissionDraft(): PermissionDraft {
  return { signedName: '', signedOn: '', signer: '', people: [blankPermissionPerson()] };
}

export const PERMISSION_SIGNER_OPTIONS: ReadonlyArray<{ value: 'subject' | 'parent_guardian'; label: string }> = [
  { value: 'subject', label: 'Signed by the person themselves' }, // DRAFT
  { value: 'parent_guardian', label: 'By their parent or guardian' }, // DRAFT
];

export const PERMISSION_NAME_MESSAGE = 'Please enter who signed it.'; // DRAFT
export const PERMISSION_DATE_MESSAGE = 'Please enter the date it was signed.'; // DRAFT
export const PERMISSION_SIGNER_MESSAGE = 'Please choose who signed it.'; // DRAFT
export const PERMISSION_PEOPLE_MESSAGE = 'Please name at least one person it covers.'; // DRAFT
export const PERMISSION_PERSON_NAME_MESSAGE = 'Each person needs a name.'; // DRAFT

/** A row with nothing typed in it is ignored; a row with anything typed needs a name. */
function personIsBlank(p: PermissionPersonDraft): boolean {
  return !p.name.trim() && !p.role.trim() && !p.email.trim() && !p.phone.trim();
}

export function permissionError(draft: PermissionDraft, now: Date = new Date()): string | null {
  if (!draft.signedName.trim()) return PERMISSION_NAME_MESSAGE;
  if (!YMD_RE.test(draft.signedOn)) return PERMISSION_DATE_MESSAGE;
  if (draft.signedOn > todayYmd(now)) return REPLY_FUTURE_MESSAGE;
  if (draft.signer === '') return PERMISSION_SIGNER_MESSAGE;
  const filled = draft.people.filter((p) => !personIsBlank(p));
  if (filled.length === 0) return PERMISSION_PEOPLE_MESSAGE;
  if (filled.some((p) => !p.name.trim())) return PERMISSION_PERSON_NAME_MESSAGE;
  return null;
}

/** The wire body (trimmed; blank optional fields omitted; at most 5 people), or null while incomplete. */
export function permissionBody(draft: PermissionDraft, now: Date = new Date()): RecordRraPermissionBody | null {
  if (permissionError(draft, now) !== null || draft.signer === '') return null;
  const verifiers = draft.people
    .filter((p) => !personIsBlank(p))
    .slice(0, MAX_PERMISSION_PEOPLE)
    .map((p) => ({
      name: p.name.trim(),
      ...(p.role.trim() ? { role: p.role.trim() } : {}),
      ...(p.email.trim() ? { email: p.email.trim() } : {}),
      ...(p.phone.trim() ? { phone: p.phone.trim() } : {}),
    }));
  return {
    signedName: draft.signedName.trim(),
    signedAt: draft.signedOn,
    signer: draft.signer,
    verifiers,
  };
}

// ---------------------------------------------------------------------------
// Convenience for the panel: the items an ask covers, from the case view
// ---------------------------------------------------------------------------

export function itemsOfVerification(record: Pick<RraCaseView, 'items'>, v: Pick<RraVerificationView, 'itemIds'>): RraItemView[] {
  const wanted = new Set(v.itemIds);
  return record.items.filter((i) => wanted.has(i.itemId));
}
